import { useRegisterSW } from 'virtual:pwa-register/react';

/**
 * PWA Update Handler
 *
 * Handles automatic updates with immediate reload when new version is available.
 * This component handles:
 * 1. Immediate update check on registration
 * 2. Update check when user returns to app (visibility change)
 * 3. Periodic update checks every 5 minutes
 * 4. Automatic reload when new SW is ready
 */
export function UpdatePrompt() {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r: ServiceWorkerRegistration | undefined) {
      if (!r) return;

      // Check for updates immediately on registration
      r.update();

      // Check for updates when user returns to app (tab becomes visible)
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          r.update();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Check for updates every 5 minutes (more frequent for faster updates)
      const intervalId = setInterval(() => {
        r.update();
      }, 5 * 60 * 1000);

      // Cleanup (though this component typically never unmounts)
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        clearInterval(intervalId);
      };
    },
    onRegisterError(error: Error) {
      console.error('SW registration error:', error);
    },
  });

  // When new version is available, reload immediately
  if (needRefresh) {
    // Set flag for splash screen
    sessionStorage.setItem('sw-updating', 'true');
    // Force update and reload
    updateServiceWorker(true);
  }

  return null;
}
